import { useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { ArrowDown, Bot, CircleSlash, Pencil, RefreshCw, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { companiesService, Company, IndustryLinkState } from '@/services/companies.service';
import { industryConfigsService } from '@/services/industry-configs.service';
import { ENTITY_META, ENTITY_ORDER, industryIcon } from '@/pages/industry-configs/industry-meta';
import { IndustryPicker } from './IndustryPicker';
import { useIndustryTemplates } from './useIndustryTemplates';
import { LINK_META, industryGroup, industryLabel } from './industry-link-meta';

type Pending =
  | { kind: 'change'; industry: string }
  | { kind: 'resolve'; keep: 'company' | 'profile' };

export function LinkStateChip({ state, className }: { state: IndustryLinkState; className?: string }) {
  const m = LINK_META[state];
  const Icon = m.icon;
  return (
    <span title={m.explain} className={clsx('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-semibold', m.chip, className)}>
      <Icon size={11} /> {m.label}
    </span>
  );
}

function SideRow({ label, sub, industry, empty }: { label: string; sub: string; industry: string | null; empty: string }) {
  const Icon = industry ? industryIcon(industry, industryGroup(industry)) : CircleSlash;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-surface px-3 py-2.5">
      <span className={clsx(
        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
        industry ? 'bg-primary-soft text-primary' : 'bg-surface-alt text-text-muted',
      )}>
        <Icon size={15} strokeWidth={1.8} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-2xs font-semibold uppercase tracking-[0.08em] text-text-muted" title={sub}>{label}</div>
        <div className={clsx('truncate text-sm', industry ? 'font-semibold text-text-primary' : 'text-text-muted')}>
          {industryLabel(industry) ?? empty}
        </div>
      </div>
    </div>
  );
}

/**
 * The company's industry as the super-admin assigned it, next to what the
 * client's CRM actually runs as — with one-click repair when they differ.
 */
export function CompanyIndustryCard({ company, onChanged }: { company: Company; onChanged: () => void }) {
  const templates = useIndustryTemplates();
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [reapplying, setReapplying] = useState(false);

  const assigned = company.industry || null;
  const crm = company.industryLink?.profileIndustry ?? null;
  const state: IndustryLinkState = company.industryLink?.state ?? (assigned ? 'awaiting_onboarding' : 'unset');
  const tpl = assigned ? templates?.[assigned] : undefined;
  const HeroIcon = assigned ? industryIcon(assigned, industryGroup(assigned)) : CircleSlash;
  const needsFix = state === 'mismatch' || state === 'crm_only';

  const confirmText = (): { title: string; message: string } => {
    if (!pending) return { title: '', message: '' };
    if (pending.kind === 'change') {
      const to = industryLabel(pending.industry);
      return {
        title: `Switch to ${to}?`,
        message: `${company.name}'s CRM switches to ${to} right away — AI assistant, departments and industry packs. ${to}'s default fields are added; nothing the company already has is removed.`,
      };
    }
    if (pending.keep === 'company') {
      return {
        title: `Keep ${industryLabel(assigned)}?`,
        message: `${company.name}'s CRM currently runs as ${industryLabel(crm)}. It switches to ${industryLabel(assigned)} — AI assistant, departments and packs follow.`,
      };
    }
    return {
      title: `Assign ${industryLabel(crm)}?`,
      message: `${industryLabel(crm)} is what ${company.name}'s CRM already uses. It becomes the assigned industry here${assigned ? `, replacing ${industryLabel(assigned)}` : ''}, and its default fields are copied in (additive).`,
    };
  };

  const run = async () => {
    if (!pending || busy) return;
    setBusy(true);
    try {
      if (pending.kind === 'change') {
        await companiesService.update(company.companyUid, { industry: pending.industry });
        toast.success(`Industry set to ${industryLabel(pending.industry)} — the CRM follows`);
        setEditing(false);
      } else {
        await companiesService.resolveIndustry(company.companyUid, pending.keep);
        toast.success('Industry linked — super-admin and CRM now match');
      }
      setPending(null);
      onChanged();
    } catch { /* interceptor toasts */ } finally { setBusy(false); }
  };

  const reapply = async () => {
    if (!assigned) return;
    setReapplying(true);
    try {
      const r = await industryConfigsService.applyToCompany(assigned, company.companyUid);
      toast.success(`${r.copied} new field${r.copied === 1 ? '' : 's'} copied · ${r.skipped} already there`);
    } catch { /* interceptor toasts */ } finally { setReapplying(false); }
  };

  const { title, message } = confirmText();

  return (
    // No overflow-hidden: the industry picker's dropdown must be able to spill out.
    <div className="rounded-xl border border-border bg-surface">
      <div className="flex items-center justify-between gap-2 border-b border-border px-5 py-3.5">
        <h3 className="text-sm font-semibold text-text-primary">Industry</h3>
        <LinkStateChip state={state} />
      </div>

      <div className="space-y-4 p-5">
        {/* Current assignment */}
        <div className="flex items-center gap-3.5">
          <span className={clsx(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl',
            assigned ? 'bg-gold-gradient text-on-primary shadow-glow' : 'bg-surface-alt text-text-muted',
          )}>
            <HeroIcon size={21} strokeWidth={1.8} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate font-display text-[17px] font-semibold text-text-primary">{industryLabel(assigned) ?? 'Not assigned'}</div>
            <div className="truncate text-xs text-text-muted">{assigned ? industryGroup(assigned) : 'The company picks one at onboarding'}</div>
          </div>
          {!editing && (
            <Button size="sm" variant="secondary" iconLeft={<Pencil size={13} />} onClick={() => setEditing(true)}>
              {assigned ? 'Change' : 'Assign'}
            </Button>
          )}
        </div>

        {editing && (
          <div className="fade-up space-y-2">
            <IndustryPicker
              value={assigned ?? ''}
              onChange={(v) => { if (v && v !== assigned) setPending({ kind: 'change', industry: v }); }}
              templates={templates ?? undefined}
            />
            <div className="flex justify-end">
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {/* Super-admin vs CRM */}
        <div className="rounded-xl bg-bg/60 p-3">
          <SideRow label="Assigned here" sub="Drives the default field template" industry={assigned} empty="Nothing assigned" />
          <div className="flex justify-center py-1 text-text-muted"><ArrowDown size={14} /></div>
           <SideRow label="CRM runs as" sub="What the client's AI bot, departments and packs use" industry={crm} empty={state === 'awaiting_onboarding' || state === 'unset' ? 'Onboarding not finished' : '—'} />
          <p className="mt-2.5 flex items-start gap-1.5 text-xs text-text-muted">
            {state === 'linked' ? <ShieldCheck size={13} className="mt-0.5 shrink-0 text-success" /> : <Bot size={13} className="mt-0.5 shrink-0" />}
            {LINK_META[state].explain}
          </p>
        </div>

        {needsFix && (
          <div className={clsx(
            'rounded-xl border p-3.5',
            state === 'mismatch' ? 'border-danger/30 bg-danger-soft' : 'border-warning/30 bg-warning-soft',
          )}>
            <p className={clsx('text-xs font-semibold', state === 'mismatch' ? 'text-danger' : 'text-warning')}>
              {state === 'mismatch' ? 'Pick which industry this company really is' : 'Link the industry the CRM already uses'}
            </p>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {state === 'mismatch' && (
                <Button size="sm" variant="secondary" onClick={() => setPending({ kind: 'resolve', keep: 'company' })}>
                  Keep {industryLabel(assigned)}
                </Button>
              )}
              <Button size="sm" onClick={() => setPending({ kind: 'resolve', keep: 'profile' })}>
                Use {industryLabel(crm)}
              </Button>
            </div>
          </div>
        )}

        {/* Default fields */}
        {assigned && (
          <div className="border-t border-border pt-4">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-2xs font-semibold uppercase tracking-[0.08em] text-text-muted">Default fields</span>
              <Link to={`/industry-configs/${assigned}`} className="text-xs font-medium text-primary hover:underline">Edit template</Link>
            </div>
            {templates === null ? (
              <div className="h-7 w-48 animate-pulse rounded-md bg-surface-alt" />
            ) : tpl && tpl.fields > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5">
                {ENTITY_ORDER.map((e) => {
                  const Icon = ENTITY_META[e].icon;
                  return (
                    <span key={e} className={clsx(
                      'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium tabular-nums',
                      tpl.byEntity[e] > 0 ? 'bg-surface-alt text-text-secondary' : 'text-text-muted',
                    )}>
                      <Icon size={12} /> {tpl.byEntity[e]} {ENTITY_META[e].label.toLowerCase()}
                    </span>
                  );
                })}
                <Button size="sm" variant="ghost" iconLeft={<RefreshCw size={13} />} loading={reapplying} onClick={reapply} className="ml-auto">
                  Re-apply
                </Button>
              </div>
            ) : (
              <p className="text-xs text-text-muted">No default fields for {industryLabel(assigned)} yet.</p>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!pending}
        title={title}
        message={message}
        onCancel={() => { if (!busy) setPending(null); }}
        onConfirm={run}
      />
    </div>
  );
}
